import { Button } from "components/DesignSystem/Button/Button";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Cross } from "components/icons/Cross";
import { FileIcon } from "components/design/FileIcon";
import { Formik } from "formik";
import { useState } from "react";
import {
  Form1120FileRef,
  useCreateForm1120ReviewMutation,
  useGetForm1120BasePromptQuery,
} from "store/apis/Task1120Tracker";
import { SelecteFile } from "types/Models/documents";
import { Task1120Tracker } from "types/Models/TaskTracker";
import { Form1120DocSelectorModal } from "./Form1120DocSelectorModal";

export const Task1120ReviewPanel = ({
  rowData,
  onReviewStarted,
}: {
  rowData: Task1120Tracker;
  onReviewStarted: (reviewId: string) => void;
}) => {
  const [selectedDocs, setSelectedDocs] = useState<Form1120FileRef[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [showBasePrompt, setShowBasePrompt] = useState(false);
  const [createReview, { isLoading }] = useCreateForm1120ReviewMutation();
  const { data: promptData } = useGetForm1120BasePromptQuery();

  const groupId = rowData.entity?.group?.uuid || "";

  const handleDocsInserted = (files: SelecteFile[]) => {
    setSelectedDocs((prev) => {
      const existingUuids = new Set(prev.map((d) => d.uuid));
      const newDocs = files
        .filter((f) => !existingUuids.has(f.uuid))
        .map((f) => ({ uuid: f.uuid, name: f.name }));
      return [...prev, ...newDocs];
    });
  };

  const removeDoc = (uuid: string) => {
    setSelectedDocs((prev) => prev.filter((d) => d.uuid !== uuid));
  };

  const getFileType = (name: string) => {
    const ext = name.split(".").pop()?.toUpperCase() || "";
    return ext;
  };

  const handleStartAgent = async () => {
    try {
      const result = await createReview({
        task_uuid: rowData.task_uuid || rowData.uuid || "",
        entity_name: rowData.entity?.group?.name || rowData.entity?.name || "",
        group_uuid: groupId,
        deep_validation: false,
        file_refs: selectedDocs,
        additional_prompt: additionalPrompt.trim() || undefined,
      }).unwrap();
      onReviewStarted(result.review_id);
    } catch {
      // RTK Query surfaces errors via isError on the mutation hook
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-5 t-p-5 t-h-full t-overflow-y-auto">
      {/* Instructions */}
      <div className="t-rounded-lg t-bg-surface-lighter-grey t-p-4 t-text-body-sm t-text-text-60">
        Please select the finalized workpapers and the draft 1120 PDF for AI
        review. Ensure all schedules are included before initiating the agent.
      </div>

      {/* Document selector */}
      <div>
        <div className="t-text-overline t-text-text-30 t-mb-2">DOCUMENTS</div>

        <Button
          customType="secondary"
          size="small"
          onClick={() => setIsPickerOpen(true)}
          type="button"
        >
          + Choose from Inkle Docs
        </Button>

        <Form1120DocSelectorModal
          isOpen={isPickerOpen}
          close={() => setIsPickerOpen(false)}
          groupId={groupId}
          alreadySelectedUuids={selectedDocs.map((d) => d.uuid)}
          onInsert={handleDocsInserted}
        />
      </div>

      {/* Selected documents list */}
      {selectedDocs.length > 0 && (
        <div>
          <div className="t-flex t-justify-between t-items-center t-mb-2">
            <span className="t-text-overline t-text-text-30">
              SELECTED DOCUMENTS
            </span>
            <span className="t-text-body-sm t-text-purple">
              {selectedDocs.length}{" "}
              {selectedDocs.length === 1 ? "document" : "documents"}
            </span>
          </div>
          <div className="t-flex t-flex-col t-gap-2">
            {selectedDocs.map(({ uuid, name }) => (
              <div
                key={uuid}
                className="t-flex t-items-center t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-3"
              >
                <FileIcon fileType={getFileType(name)} size="32" />
                <div className="t-flex-1 t-min-w-0">
                  <div className="t-text-body-sm t-text-text-100 t-truncate">
                    {name}
                  </div>
                </div>
                <Button
                  customType="ghost_icon"
                  size="small"
                  type="button"
                  onClick={() => removeDoc(uuid)}
                >
                  <Cross />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review prompt */}
      <div>
        <div className="t-text-overline t-text-text-30 t-mb-2">
          REVIEW PROMPT
        </div>

        <Button
          customType="ghost"
          size="small"
          type="button"
          onClick={() => setShowBasePrompt((v) => !v)}
        >
          <span>{showBasePrompt ? "▼" : "▶"}</span>
          <span>{showBasePrompt ? "Hide" : "View"} base prompt</span>
        </Button>

        {showBasePrompt && (
          <pre className="t-rounded t-bg-neutral-100 t-text-neutral-10 t-p-3 t-text-body-xs t-font-mono t-whitespace-pre-wrap t-mb-3 t-overflow-y-auto t-max-h-48">
            {promptData?.prompt}
          </pre>
        )}

        <Formik initialValues={{ additionalPrompt: "" }} onSubmit={() => {}}>
          <TextArea
            name="additionalPrompt"
            label="Additional instructions (optional)"
            rows={4}
            placeholder="e.g. Focus on Schedule L discrepancies. Flag any SAFE note classification issues."
            value={additionalPrompt}
            onChange={(e) => setAdditionalPrompt(e.target.value)}
            block
          />
        </Formik>
      </div>

      {/* Actions */}
      <div className="t-mt-auto">
        <Button
          customType="primary"
          block
          disabled={selectedDocs.length === 0 || isLoading}
          isLoading={isLoading}
          onClick={handleStartAgent}
        >
          ▶ Start Agent
        </Button>
      </div>
    </div>
  );
};
